/**
 * Los NOMBRES de los eventos de producto de embajadores-web.
 *
 * `snake_case` con el área delante (`auth_`, `app_`, `nav_`, `chat_`,
 * `voice_`, `memory_`, `mechanic_`, `work_`): así se agrupan solos en el
 * análisis. El nombre es el hecho; el resto viaja en `properties`. Los que
 * manda el SDK por su cuenta (`page_view`, `page_leave`, `click`…) no están
 * aquí: son suyos.
 *
 * Nadie escribe un nombre a mano: se emiten con los helpers de
 * `analytics.ts`, que son los que saben qué propiedades lleva cada uno.
 */
export const EmbajadoresEvents = {
  // ── Sesión ─────────────────────────────────────────────────────────────
  AUTH_SIGN_IN_FAILED: "auth_sign_in_failed",
  AUTH_SESSION_EXPIRED: "auth_session_expired",
  AUTH_ACCESS_DENIED: "auth_access_denied",
  AUTH_LOGOUT: "auth_logout",
  APP_READY: "app_ready",
  APP_FIRST_ACTION: "app_first_action",
  APP_API_ERROR: "app_api_error",

  // ── Navegación ─────────────────────────────────────────────────────────
  NAV_MODE_SWITCHED: "nav_mode_switched",
  NAV_SECTION_CLICKED: "nav_section_clicked",
  NAV_SIDEBAR_TOGGLED: "nav_sidebar_toggled",
  NAV_SIDEBAR_PANEL_CHANGED: "nav_sidebar_panel_changed",
  NAV_SETTINGS_OPENED: "nav_settings_opened",
  NAV_USAGE_METER_CLICKED: "nav_usage_meter_clicked",
  SETTINGS_THEME_CHANGED: "settings_theme_changed",

  // ── Conversaciones ─────────────────────────────────────────────────────
  CHAT_CONVERSATION_NEW: "chat_conversation_new",
  CHAT_CONVERSATION_OPENED: "chat_conversation_opened",
  CHAT_CONVERSATION_OPEN_FAILED: "chat_conversation_open_failed",
  CHAT_CONVERSATION_RENAMED: "chat_conversation_renamed",
  CHAT_CONVERSATION_DELETED: "chat_conversation_deleted",

  // ── Turnos ─────────────────────────────────────────────────────────────
  CHAT_MESSAGE_SENT: "chat_message_sent",
  CHAT_TURN_COMPLETED: "chat_turn_completed",
  CHAT_TURN_FAILED: "chat_turn_failed",
  CHAT_TURN_STOPPED: "chat_turn_stopped",
  CHAT_ERROR_DISMISSED: "chat_error_dismissed",

  // ── Confirmaciones y preguntas ─────────────────────────────────────────
  CHAT_CONFIRMATION_DECIDED: "chat_confirmation_decided",
  CHAT_CONFIRMATION_RESULT: "chat_confirmation_result",
  CHAT_QUESTIONS_ANSWERED: "chat_questions_answered",
  CHAT_ABANDON_PROMPT_SHOWN: "chat_abandon_prompt_shown",
  CHAT_ABANDON_PROMPT_RESOLVED: "chat_abandon_prompt_resolved",

  // ── Adjuntos y voz ─────────────────────────────────────────────────────
  CHAT_ATTACHMENT_UPLOADED: "chat_attachment_uploaded",
  CHAT_ATTACHMENT_FAILED: "chat_attachment_failed",
  CHAT_ATTACHMENT_REJECTED: "chat_attachment_rejected",
  CHAT_ATTACHMENT_REMOVED: "chat_attachment_removed",
  VOICE_STARTED: "voice_started",
  VOICE_COMPLETED: "voice_completed",
  VOICE_CANCELLED: "voice_cancelled",
  VOICE_FAILED: "voice_failed",

  // ── Lo que dibuja el agente ────────────────────────────────────────────
  CHAT_FILE_DOWNLOADED: "chat_file_downloaded",
  CHAT_RESOURCE_LINK_OPENED: "chat_resource_link_opened",
  CHAT_TABLE_MORE_LOADED: "chat_table_more_loaded",
  CHAT_OPEN_IN_CHAT: "chat_open_in_chat",
  CHAT_COMPONENT_UNKNOWN: "chat_component_unknown",
  CHAT_BUBBLE_OPENED: "chat_bubble_opened",
  CHAT_BUBBLE_CLOSED: "chat_bubble_closed",

  // ── Memoria y mecánicas ────────────────────────────────────────────────
  MEMORY_NOTE_OPENED: "memory_note_opened",
  MEMORY_NOTE_SAVED: "memory_note_saved",
  MEMORY_NOTE_DELETED: "memory_note_deleted",
  MECHANIC_OPENED: "mechanic_opened",
  MECHANIC_LIST_EXPANDED: "mechanic_list_expanded",
  MECHANIC_EDIT_STARTED: "mechanic_edit_started",
  MECHANIC_DIFF_PREVIEWED: "mechanic_diff_previewed",
  MECHANIC_HANDED_TO_AGENT: "mechanic_handed_to_agent",

  // ── Work (todos llevan `section`) ──────────────────────────────────────
  WORK_PERIOD_CHANGED: "work_period_changed",
  WORK_ALERT_ACTION: "work_alert_action",
  WORK_OVERVIEW_EXPANDED: "work_overview_expanded",
  WORK_BATCH_OPENED: "work_batch_opened",
  WORK_SEARCH: "work_search",
  WORK_LIST_MORE_LOADED: "work_list_more_loaded",
  WORK_RECORD_OPENED: "work_record_opened",
  WORK_RECORD_CREATE_STARTED: "work_record_create_started",
  WORK_RECORD_SAVED: "work_record_saved",
  WORK_RECORD_DELETED: "work_record_deleted",
  WORK_PARTICIPANT_ACTIVE_TOGGLED: "work_participant_active_toggled",
  WORK_ENROLLMENT_SAVED: "work_enrollment_saved",
  WORK_TABLE_SORTED: "work_table_sorted",
  WORK_PRICES_SYNCED: "work_prices_synced",
  WORK_PERMISSION_BLOCKED: "work_permission_blocked",
} as const;

export type EmbajadoresEvent =
  (typeof EmbajadoresEvents)[keyof typeof EmbajadoresEvents];

/**
 * Las secciones de Work, como dimensión estable. Son las `key` de
 * `DASHBOARD_SECTIONS` más el detalle de un lote, que no tiene entrada de
 * menú.
 */
export type WorkSection =
  | "overview"
  | "participants"
  | "positions"
  | "regions"
  | "kpis"
  | "usage"
  | "batch";

/** Dónde se dibuja el chat: a pantalla completa o en la burbuja de Work. */
export type ChatSurfaceName = "chat" | "bubble";
